// =============================================================================
// Documents Sidebar - Navigation ClickUp-like pour le module Documents
// Filtres, Favoris, Wiki populaires
// =============================================================================

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDocFavorites } from "@/modules/documents/hooks/useDocFavorites";
import { cn } from "@/lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Files,
  User,
  Users,
  Lock,
  ShieldCheck,
  Archive,
  Star,
  Plus,
  StickyNote,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface DocumentsSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

type DocFilter = "all" | "mine" | "shared" | "private" | "restricted" | "meeting-notes" | "archived";

const NAV_ITEMS: { id: DocFilter; icon: React.ElementType; label: string }[] = [
  { id: "all", icon: Files, label: "Tous les documents" },
  { id: "mine", icon: User, label: "Mes documents" },
  { id: "shared", icon: Users, label: "Partagé avec moi" },
  { id: "private", icon: Lock, label: "Privé" },
  { id: "restricted", icon: ShieldCheck, label: "Restreint" },
  { id: "meeting-notes", icon: StickyNote, label: "Notes de réunion" },
  { id: "archived", icon: Archive, label: "Archivé" },
];

const COLORS = {
  bg: "#FFFFFF",
  bgHover: "#F0F9FF",
  bgActive: "#E0F2FE",
  border: "#E2E8F0",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  accent: "#06B6D4", // Cyan matching the module color
};

export function DocumentsSidebar({ collapsed, onToggle }: DocumentsSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { docFavorites, wikiFavorites } = useDocFavorites();

  // Extract filter from search params
  const searchParams = new URLSearchParams(location.search);
  const currentFilter = (searchParams.get("filter") as DocFilter) || "all";
  const isOnDocuments = location.pathname === "/documents";

  const handleFilterClick = (filter: DocFilter) => {
    if (filter === "all") {
      navigate("/documents");
    } else {
      navigate(`/documents?filter=${filter}`);
    }
  };

  const handleCreate = () => {
    // Navigate to documents, the DocsHub will handle creation
    navigate("/documents?action=create");
  };

  if (collapsed) {
    return (
      <aside
        className="flex flex-col w-12 shrink-0 border rounded-xl h-full"
        style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
      >
        <div className="h-14 flex items-center justify-center border-b" style={{ borderColor: COLORS.border }}>
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8"
            style={{ color: COLORS.textSecondary }}
            onClick={onToggle}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col items-center py-3 gap-1">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isOnDocuments && currentFilter === item.id;

              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleFilterClick(item.id)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
                      style={{
                        backgroundColor: active ? COLORS.bgActive : "transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (!active) e.currentTarget.style.backgroundColor = COLORS.bgHover;
                      }}
                      onMouseLeave={(e) => {
                        if (!active) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <Icon
                        className="h-4 w-4"
                        style={{
                          color: active ? COLORS.accent : COLORS.textSecondary,
                          strokeWidth: active ? 2.5 : 2,
                        }}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    style={{ backgroundColor: "#1E293B", color: "#FFFFFF", borderColor: "#334155" }}
                  >
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="flex flex-col w-56 shrink-0 border rounded-xl h-full"
      style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
    >
      {/* Header */}
      <div className="h-14 flex items-center justify-between px-4 border-b" style={{ borderColor: COLORS.border }}>
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 shrink-0" style={{ color: COLORS.accent }} />
          <span className="font-semibold text-sm truncate" style={{ color: COLORS.textPrimary }}>
            Documents
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="w-7 h-7 shrink-0"
            style={{ color: COLORS.textSecondary }}
            onClick={onToggle}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="w-7 h-7 shrink-0" style={{ color: COLORS.textSecondary }}>
                <Plus className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              <DropdownMenuItem onClick={handleCreate} className="text-xs gap-2">
                <FileText className="h-3.5 w-3.5" />
                Nouveau document
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Navigation filters — never scrolls */}
        <nav className="p-2 space-y-0.5 shrink-0">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isOnDocuments && currentFilter === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleFilterClick(item.id)}
                className={cn(
                  "flex items-center gap-3 w-full px-3 py-2 rounded-lg text-[12px] transition-colors cursor-pointer text-left",
                )}
                style={{
                  backgroundColor: active ? COLORS.bgActive : "transparent",
                  color: active ? COLORS.accent : COLORS.textPrimary,
                  fontWeight: active ? 500 : 400,
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.backgroundColor = "#F1F5F9";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.backgroundColor = active ? COLORS.bgActive : "transparent";
                }}
              >
                <Icon
                  className="h-4 w-4 shrink-0"
                  style={{
                    color: active ? COLORS.accent : COLORS.textSecondary,
                    strokeWidth: active ? 2.5 : 2,
                  }}
                />
                <span className="truncate flex-1">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Separator */}
        <div className="mx-3 my-2 border-t shrink-0" style={{ borderColor: COLORS.border }} />

        {/* Favoris section — scrolls independently */}
        <div className="px-3 shrink-0 flex flex-col min-h-0" style={{ maxHeight: '35%' }}>
          <p
            className="text-[11px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 shrink-0"
            style={{ color: COLORS.textSecondary }}
          >
            <Star className="h-3 w-3" style={{ fill: '#FBBF24', color: '#FBBF24' }} />
            Favoris
          </p>
          {docFavorites.length === 0 ? (
            <div
              className="rounded-lg border border-dashed p-4 flex flex-col items-center text-center"
              style={{ borderColor: "#E2E8F0" }}
            >
              <Star className="h-5 w-5 mb-1.5" style={{ color: "#FBBF24" }} />
              <p className="text-[11px] leading-tight" style={{ color: COLORS.textSecondary }}>
                Mettez une étoile sur un Document pour le voir apparaître ici
              </p>
            </div>
          ) : (
            <div className="space-y-0.5 overflow-y-auto min-h-0 [scrollbar-width:thin] [scrollbar-color:hsl(0_0%_80%)_transparent] [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
              {docFavorites.map(fav => (
                <button
                  key={fav.id}
                  onClick={() => navigate(`/documents/${fav.document_id}`)}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-[12px] transition-colors text-left hover:bg-muted/50"
                  style={{ color: COLORS.textPrimary }}
                >
                   <span className="text-sm shrink-0">{fav.document_icon}</span>
                   <span className="truncate flex-1">{fav.document_title}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="mx-3 my-2 border-t shrink-0" style={{ borderColor: COLORS.border }} />

        {/* Wiki populaires section — scrolls independently */}
        <div className="px-3 pb-3 flex flex-col min-h-0 flex-1 overflow-hidden">
          <p
            className="text-[11px] font-semibold uppercase tracking-wider mb-2 shrink-0 flex items-center gap-1.5"
            style={{ color: COLORS.textSecondary }}
          >
            <Star className="h-3 w-3" style={{ fill: '#A78BFA', color: '#A78BFA' }} />
            Wikis favoris
          </p>
          {wikiFavorites.length === 0 ? (
            <div
              className="rounded-lg border border-dashed p-4 flex flex-col items-center text-center"
              style={{ borderColor: "#E2E8F0" }}
            >
              <BookOpen className="h-5 w-5 mb-1.5" style={{ color: "#A78BFA" }} />
              <p className="text-[11px] leading-tight" style={{ color: COLORS.textSecondary }}>
                Mettez une étoile sur un Wiki pour le voir apparaître ici
              </p>
            </div>
          ) : (
            <div className="space-y-0.5 overflow-y-auto min-h-0 [scrollbar-width:thin] [scrollbar-color:hsl(0_0%_80%)_transparent] [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
              {wikiFavorites.map(fav => (
                <button
                  key={fav.id}
                  onClick={() => navigate(`/wiki/${fav.document_id}`)}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-left transition-colors hover:bg-[#F0F9FF] group"
                >
                  <span className="text-sm shrink-0">{fav.document_icon}</span>
                  <span className="text-[12px] truncate" style={{ color: COLORS.textPrimary }}>
                    {fav.document_title}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t" style={{ borderColor: COLORS.border }}>
        <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
          📄 Documentation & Wikis
        </p>
      </div>
    </aside>
  );
}
